import { composePlan } from './compose-plan.js';
import { cellContent, frameLayout } from './fit.js';
import { createProgram, createSourceTexture, deleteProgram } from './gl-context.js';
import { apply, invert, rectToQuad, toColumnMajor, DegenerateQuadError } from './homography.js';
import { quadToStage } from './layout.js';
import { naturalSizeOf, needsRealloc } from './source.js';
/**
 * 合成器を作る。寸法が取れない・WebGL2 が使えないときは `null`。
 *
 * **重い準備をここで済ませる** —— コンテキスト・シェーダ・**背景と障害物マスクの層**。
 * `draw()` は動画枠を描いて重ねるだけになる。
 */
export function createComposer(options) {
    const plan = composePlan(options.template);
    if (plan === null)
        return null;
    const canvas = document.createElement('canvas');
    canvas.width = plan.size.width;
    canvas.height = plan.size.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null)
        return null;
    const gl = createGl(plan);
    if (gl === null)
        return null;
    let program;
    try {
        program = createProgram(gl.context);
    }
    catch {
        gl.dispose();
        return null;
    }
    /*
     * **背景と障害物マスクは動かない。** テンプレートも背景画像も書き出しの間は変わらないので、
     * **層を 1 度だけ作って毎フレーム `drawImage` する。**
     *
     * ぼかしマスクは白ポリゴンのラスタライズと合成を伴うため、
     * **毎フレーム描くと 1800 回払うことになる。**
     */
    const masks = options.background === null ? null : buildMaskLayer(plan, options.background);
    /*
     * **背景も 1 度だけ焼く**(#181 の実測)。
     *
     * 背景写真は出力寸法と**一致しない**(例: 1800×1200 の写真を 1920×1080 へ)。
     * `drawImage` に寸法を渡すと**毎フレーム拡大縮小が走り**、利用者の環境で
     * **合成が 62.98ms/枚**になっていた —— 30fps の予算(33ms)を超え、
     * **書き出しが 10fps に落ちていた**(実測: 30 秒の出力に 301 枚しか入らない)。
     *
     * ここで出力寸法に焼いておけば、毎フレームは**等倍のコピー**で済む。
     */
    const backgroundLayer = options.background === null ? null : bakeBackground(plan, options.background);
    const textures = new Map();
    return {
        canvas,
        draw() {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            // ① 背景。**焼いた層を等倍で置く**(拡大縮小は `bakeBackground` で済ませてある)。
            if (backgroundLayer !== null)
                ctx.drawImage(backgroundLayer, 0, 0);
            // ② 動画枠。
            drawFrames(gl.context, program, plan, options.sourceAt, textures);
            ctx.drawImage(gl.canvas, 0, 0);
            // ③ 障害物マスク。**動画より手前**に背景を重ねて遮蔽を作る(設計書 8.4)。
            if (masks !== null)
                ctx.drawImage(masks, 0, 0);
        },
        dispose() {
            for (const entry of textures.values())
                gl.context.deleteTexture(entry.texture);
            textures.clear();
            deleteProgram(program);
            gl.dispose();
        },
    };
}
/**
 * いまの状態を 1 枚の canvas に描く(#180)。
 *
 * 寸法が取れない・WebGL2 が使えないときは `null`。
 * **呼び出し側は「書き出せない」と伝える**(黙って壊れない —— ADR-0036 決定 8)。
 *
 * **返るのは新しい canvas。** 呼び出し側が `toBlob` するなり `VideoFrame` に包むなりできる。
 * WebGL のリソースは戻る前に解放するが、**2D canvas は別物なので残る。**
 */
export function composeFrame(options) {
    const composer = createComposer(options);
    if (composer === null)
        return null;
    try {
        composer.draw();
        return composer.canvas;
    }
    finally {
        composer.dispose();
    }
}
function createGl(plan) {
    const canvas = document.createElement('canvas');
    canvas.width = plan.size.width;
    canvas.height = plan.size.height;
    /*
     * **書き出し用は `preserveDrawingBuffer: true`。**
     *
     * 表示用([`gl-context.ts`](./gl-context.ts))は毎フレーム描き直すので保持しないが、
     * こちらは**描いたあとに `drawImage` で吸い出す**。既定(`false`)でも同じ実行ターン内なら
     * 読めるはずだが、**読めなければ絵が丸ごと落ちる**ので、確実な側に倒す。
     *
     * **表示用の設定は変えていない。** これは書き出しのために作る別のコンテキストである。
     */
    const context = canvas.getContext('webgl2', {
        alpha: true,
        premultipliedAlpha: false,
        antialias: true,
        preserveDrawingBuffer: true,
        powerPreference: 'default',
    });
    if (context === null)
        return null;
    return {
        canvas,
        context,
        dispose: () => {
            context.getExtension('WEBGL_lose_context')?.loseContext();
        },
    };
}
/**
 * 動画枠をすべて描く。**背景は透明。**
 *
 * 表示と同じシェーダ・同じユニフォームを使う。違うのは 2 点だけ:
 *
 * - **倍率は常に 1**(表示は DPR 上限で決まる —— #174)
 * - **ハイライトは常に 0**(選択の強調は編集画面のもの)
 */
function drawFrames(gl, program, plan, sourceAt, textures) {
    gl.viewport(0, 0, plan.size.width, plan.size.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(program.program);
    gl.bindVertexArray(program.vao);
    gl.uniform2f(program.uniforms.resolution, plan.size.width, plan.size.height);
    // **等倍。** 表示の倍率(#174)は入れ物と DPR の話で、書き出しには無い。
    gl.uniform1f(program.uniforms.dpr, 1);
    gl.uniform1i(program.uniforms.texture, 0);
    // 選択の強調は編集画面のもの。書き出しには出さない。
    gl.uniform1f(program.uniforms.highlight, 0);
    for (const frame of plan.frames) {
        drawFrame(gl, program, plan.transform, frame.id, frame.quad, frame.grid, sourceAt, textures);
    }
    gl.bindVertexArray(null);
}
/**
 * 枠を 1 つ描く。**分割していればセルごと**(#152)。
 *
 * **ホモグラフィは枠に 1 つだけ作って全セルで使い回す**
 * ([ADR-0029](../../../docs/adr/0029-panel-split-grid.md) の決定 3)——
 * セルごとに作ると境界がわずかにずれ、**継ぎ目に隙間が出る。**
 * 表示側([`webgl-renderer.ts`](./webgl-renderer.ts) の `drawFrame`)と同じ手順である。
 */
function drawFrame(gl, program, transform, frameId, quad, grid, sourceAt, textures) {
    const stageQuad = quadToStage(quad, transform);
    const layout = frameLayout(stageQuad, grid);
    // 潰れたクアッドでは描かない。
    if (layout === null)
        return;
    const { panel, cells } = layout;
    let toStage;
    let inverse;
    try {
        toStage = rectToQuad(panel.width, panel.height, stageQuad);
        // **`invert` も同じ try に入れる。** 写す先が決まっても行列が特異になれば投げる。
        inverse = invert(toStage);
    }
    catch (error) {
        if (error instanceof DegenerateQuadError)
            return;
        throw error;
    }
    // 盤面の座標系は全セルで共通。**枠ごとに 1 度だけ渡す。**
    gl.uniformMatrix3fv(program.uniforms.inverse, false, toColumnMajor(inverse));
    gl.uniform2f(program.uniforms.panelSize, panel.width, panel.height);
    for (const cell of cells) {
        drawCell(gl, program, frameId, cell, toStage, sourceAt, textures);
    }
}
/** セルを 1 つ描く。素材が無い・準備前なら描かない。 */
function drawCell(gl, program, frameId, cell, toStage, sourceAt, textures) {
    const source = sourceAt(frameId, cell.index);
    if (source === null)
        return;
    // 準備が済む前は自然サイズが 0(メタデータ待ちの `<video>` / 読み込み前の `<img>`)。
    const size = naturalSizeOf(source);
    if (size.width === 0 || size.height === 0)
        return;
    // **写し元は盤面の矩形**(#147 / ADR-0028)。余りは黒帯になる。
    const content = cellContent(cell, size);
    if (content === null)
        return;
    /*
     * **テクスチャは素材ごとに使い回す**(#168 と同じ考え方)。
     *
     * 連続書き出しでは同じ素材を**何百回も**描く。毎回作って捨てると、
     * 確保と解放だけで時間を食う。**同じファイルを複数のセルで共有している場合も
     * 1 つで済む** —— 呼び出し側が同じ要素を返すため、`Map` の鍵が一致する。
     */
    let entry = textures.get(source);
    if (entry === undefined) {
        try {
            entry = { texture: createSourceTexture(gl), allocated: null };
        }
        catch {
            return;
        }
        textures.set(source, entry);
    }
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, entry.texture);
    /*
     * **動画は毎フレーム転送する。** 表示側は「新しいフレームが来たときだけ」に
     * 絞っている([`shouldUpload`](./source.ts))が、**書き出しは時刻を指定して
     * シークしながら描く**ので、「来たかどうか」ではなく**常に今の中身**が要る。
     *
     * **1 度目は `texImage2D`、以後は `texSubImage2D`**(#168 と同じ)——
     * 前者は毎回テクスチャの形を作り直すが、後者は既にある格納域に書き込むだけで済む。
     */
    if (needsRealloc(entry.allocated, size)) {
        /*
         * **確保できたかを確かめてから記録する**(#170)。
         *
         * 中身の無い動画(`readyState < 2`)や上限を超えた寸法では `INVALID_VALUE` になり、
         * **テクスチャは確保されない。** それを確保済みと記録すると、以後 `texSubImage2D` に進んで
         * **level 0 が無いので永久に書けない** —— **セルが真っ暗になる。**
         */
        gl.getError(); // 前の誤りを自分のものと取り違えないよう掃除する
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        if (gl.getError() !== gl.NO_ERROR) {
            /*
             * **確保できなかった。テクスチャごと捨てて、次のフレームで作り直す。**
             *
             * **残して再挑戦しても戻らない**(#181 で踏んだ)—— 一度 level 0 の確保に
             * 失敗したテクスチャは "incomplete" のままで、同じ `texImage2D` を繰り返しても
             * **サンプリングは黒を返し続ける。** その結果、**書き出しが最初から最後まで
             * 真っ黒**になった。
             *
             * 捨てておけば次のフレームで新しいテクスチャから始められる。
             */
            gl.deleteTexture(entry.texture);
            textures.delete(source);
            return;
        }
        entry.allocated = { width: size.width, height: size.height };
    }
    else {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }
    gl.uniform4f(program.uniforms.contentRect, content.x, content.y, content.width, content.height);
    // **セルの 4 隅を枠共通のホモグラフィで写す。** 隣り合うセルは盤面座標を
    // 厳密に共有しているので、写したあとも一致する —— 継ぎ目に隙間が出ない。
    const [a, b, c, d] = cell.corners.map((corner) => apply(toStage, corner));
    if (a === undefined || b === undefined || c === undefined || d === undefined)
        return;
    gl.bindBuffer(gl.ARRAY_BUFFER, program.positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([...a, ...b, ...c, ...a, ...c, ...d]), gl.DYNAMIC_DRAW);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
}
// ---------------------------------------------------------------------------
// 障害物マスク(静的な層)
// ---------------------------------------------------------------------------
/**
 * 背景を出力寸法に焼く(#181)。
 *
 * **毎フレームの拡大縮小をやめるため。** 背景写真は出力寸法と一致しないことがあり、
 * `drawImage` に寸法を渡すと**そのたびに再標本化**が走る ——
 * 実測で**合成の大半を占めていた。**
 *
 * 焼けないとき(2D が取れない)は `null`。**呼び出し側は背景無しで描く**
 * より、元の画像をそのまま使うほうがよいので、その場合だけ従来どおり渡す。
 */
function bakeBackground(plan, background) {
    const layer = document.createElement('canvas');
    layer.width = plan.size.width;
    layer.height = plan.size.height;
    const ctx = layer.getContext('2d');
    if (ctx === null)
        return background;
    ctx.drawImage(background, 0, 0, layer.width, layer.height);
    return layer;
}
/**
 * 障害物マスクだけを描いた**透明な層**を作る。
 *
 * **背景画像を障害物の形に切り抜いて動画より手前に重ねる**(設計書 8.4 / PoC 3.6)——
 * 動画を隠すのではなく「背景が手前にある」ように見せるので、
 * 柱の向こうを動画が通り過ぎる表現になる。
 *
 * **1 度だけ作る。** 背景もテンプレートも書き出しの間は変わらない。
 */
function buildMaskLayer(plan, background) {
    if (plan.masks.length === 0)
        return null;
    const layer = document.createElement('canvas');
    layer.width = plan.size.width;
    layer.height = plan.size.height;
    const ctx = layer.getContext('2d');
    if (ctx === null)
        return null;
    for (const mask of plan.masks) {
        drawMask(ctx, mask, background, plan);
    }
    return layer;
}
/** 障害物マスクを 1 つ描く。 */
function drawMask(ctx, mask, background, plan) {
    const { width, height } = plan.size;
    const points = quadToStage(mask.points, plan.transform);
    if (points.length < 3)
        return;
    if (mask.kind === 'clip') {
        ctx.save();
        ctx.beginPath();
        tracePath(ctx, points);
        ctx.clip();
        ctx.drawImage(background, 0, 0, width, height);
        ctx.restore();
        return;
    }
    /*
     * **ぼかすのは輪郭(アルファ)だけ。写真の中身はぼかさない**
     * ([ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md))。
     *
     * だから「切り抜いた絵に `filter` を掛ける」のではなく、
     * **白いポリゴンをぼかしたものをアルファにして背景を流し込む** ——
     * 表示側が `mask-image` に白ポリゴン + `feGaussianBlur` の SVG を渡しているのと同じ考え方で、
     * CSS の `blur(Npx)` と `feGaussianBlur` の `stdDeviation=N` は同義なので**係数もそのまま**使える。
     */
    const off = document.createElement('canvas');
    off.width = width;
    off.height = height;
    const octx = off.getContext('2d');
    if (octx === null)
        return;
    octx.filter = `blur(${String(mask.stdDeviation)}px)`;
    octx.fillStyle = '#fff';
    octx.beginPath();
    tracePath(octx, points);
    octx.fill();
    // ここから先はぼかさない —— 流し込む写真がぼけてしまう。
    octx.filter = 'none';
    octx.globalCompositeOperation = 'source-in';
    octx.drawImage(background, 0, 0, width, height);
    ctx.drawImage(off, 0, 0);
}
/** 多角形のパスを引く(閉じるのは呼び出し側の `clip` / `fill` に任せる)。 */
function tracePath(ctx, points) {
    const [first, ...rest] = points;
    if (first === undefined)
        return;
    ctx.moveTo(first[0], first[1]);
    for (const [x, y] of rest)
        ctx.lineTo(x, y);
    ctx.closePath();
}
//# sourceMappingURL=compose.js.map